import { Dispatch, AnyAction } from "redux";

import { CtxType } from "../common/types";
import IO, { IoResponse } from "../IO";

/**
 * Redux异步状态
 */
export enum ReduxHelperStatus {
  INITIAL = "INITIAL",
  LOADING = "LOADING",
  COMPLETE = "COMPLETE",
}

export type ActionFuncResponse<V = any> = Promise<IoResponse<V>> | IoResponse<V>;

/**
 * Redux 异步 State Item
 */
export interface ReduxStateItem<P = any, V = any> {
  status: ReduxHelperStatus;
  param: P;
  value: V;
  isReady: boolean;
}

/**
 * Redux 异步 State 集合
 */
export interface ReduxStateObj<P = any, V = any> {
  [x: string]: ReduxStateItem<P, V>;
}

/**
 * 异步 Action
 */
export interface ActionObj<P = any, V = any> extends AnyAction {
  payload?: { param: P; value: V };
}

/**
 * 异步 Action 方法
 */
export type ActionFunc<P = any, V = any, E = any> = (
  param?: P | null
) => (
  dispatch: Dispatch<ReduxStateObj<P, V>>,
  getState: () => ReduxStateObj<P, V>,
  extraArgument: E
) => ActionFuncResponse<V>;

/**
 * Redux Helper item对象
 */
export type ReduxHelperItemObj<P = any, V = any, E = any> = {
  action: ActionFunc<P, V, E>;
  state: ReduxStateObj<P, V>;
  reducer: (state: ReduxStateObj<P, V>, action: ActionObj<P, V>) => ReduxStateObj<P, V>;
};

export type CtxIncludeIo = CtxType & {
  io: IO;
};

/**
 * Async方法的参数方法类型
 */
export type AsyncParamFunc<P = any, V = any, E = any> = (
  param: P,
  ctx: E | CtxIncludeIo,
  dispatch: Dispatch<ReduxStateObj<P, V>>,
  getState: () => ReduxStateObj<P, V>
) => ActionFuncResponse<V>;

/**
 * Async方法
 */
export type AsyncFunc<P = any, V = any, E = any> = (
  task: AsyncParamFunc<P, V, E>
) => ReduxHelperItemObj<P, V, E>;

/**
 * Redux Helper item方法
 */
export type ReduxHelperItemFunc<P = any, V = any, E = any> = ({
  async,
}: {
  async: AsyncFunc<P, V>;
}) => ReduxHelperItemObj<P, V, E>;

export type ReduxHelperItem<P = any, V = any, E = any> =
  | ReduxHelperItemFunc<P, V, E>
  | ReduxHelperItemObj<P, V, E>;

export type ReducerFunc<S = any> = (state: S, action: ActionObj) => S;
